import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { remove } from "lodash";
import { Subject } from "rxjs";
import { ToastService } from "~app/common/components/toast/toast.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { BusySpinnerComponent } from "../../../../busy/busy-spinner/busy-spinner.component";
import { CheckboxComponent } from "../../../../checkbox/checkbox.component";
import { IconButtonComponent } from "../../../../icon-button/icon-button.component";
import { IMapComponent, MapExtent } from "../../../map.models";
import { IMapGeometry, IMapGeometryWicket, IMapSettings } from "../../../map.settings";
import { IGeometryMetadata, MapEditService } from "../../../services/map-edit.service";
import { MapQueryService } from "../../../services/map-query.service";
import { SpatialService } from "../../../services/spatial.service";
import { InformationPopupComponent } from "./information-popup/information-popup.component";
import { GeometryInformation } from "./models/edit-models";

@Component({
    selector: "rask-edit-menu",
    templateUrl: "./edit-menu.component.html",
    styleUrls: ["./edit-menu.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BusySpinnerComponent,
        NgbTooltip,
        CheckboxComponent,
        FormsModule,
        IconButtonComponent,
        InformationPopupComponent
    ]
})
export class EditMenuComponent implements OnInit, OnDestroy {
    @Input() geometries: GeometryInformation[] = [];
    @Input() map: IMapComponent;
    @Input() mapSettings: IMapSettings;
    @Input() point: IMapGeometry;

    @Input() readonly: boolean;

    protected checkedGeoInfo: GeometryInformation[];
    protected loading: boolean;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly mapEditService = inject(MapEditService);
    private readonly mapQueryService = inject(MapQueryService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly spatialService = inject(SpatialService);
    private readonly toastService = inject(ToastService);

    private previewThrottle: any;

    canCopy(): boolean {
        return this.mapEditService.canCopyFromInternal();
    }

    canEdit(): boolean {
        return this.geometries.filter(g => g.checked).length === 1 && !this.readonly;
    }

    canMerge(): boolean {
        return this.geometries.filter(g => g.checked).length > 1 && !this.readonly;
    }

    canPaste(): boolean {
        return this.mapEditService.canPasteFromInternal() && !this.readonly;
    }

    canZoom(): boolean {
        return this.hasSelected();
    }

    close($event): void {
        this.clearChecked();
        this.map?.clearPreview();
        this.geometries.splice(0);
        this.map?.closePopups();
        this.map?.closeWidget("edit-menu");
    }

    closeGeometry(gi): void {
        remove(this.geometries, g => g === gi);
        this.preview();
    }

    copy(): void {
        const geometryinfos = this.getSelected();
        this.mapEditService.copyToInternalAndWindowsClipboard(geometryinfos, true, true);
        this.toastService.add(geometryinfos.length > 1 ? "Objekten kopierade" : "Objektet kopierat");
    }

    edit(): void {
        const drawLayer = this.map.createDrawLayer();
        const geometries = this.geometries.filter(g => g.checked).map(g => g.geometry);
        this.map.setGeometries(drawLayer.id, geometries);
        setTimeout(() => {
            this.map.getSketch().edit(drawLayer, geometries[0], (newgeometry) => {
                if (newgeometry?.wkt) {
                    (geometries[0] as IMapGeometryWicket).wkt = newgeometry.wkt;
                    this.map.removeDrawLayer(drawLayer.id);
                    this.preview();
                }
            });
        }, 50);
    }

    geometryChecked(geometry: GeometryInformation, event): void {
        geometry.checked = event;
        this.preview();
        this.updateCheckedObjects();
    }

    getShortLabel(label: string): string {
        return label?.length > 28 ? label.substring(0, 27) : label;
    }

    hasSelected(): boolean {
        return this.geometries?.some(g => g.checked);
    }

    merge(): void {
        const selected = this.getSelected();
        const joined = this.spatialService.join(selected.map(s => s.geometry));
        joined.id = GuidUtils.newGuid();
        joined.label = selected.map(s => s.label).join(" + ");
        joined.visible = true;
        joined.tag = { geometries: selected.map(s => s.geometry) };
        this.map.clearPreview();
        this.map.preview([joined], [
            200,
            200,
            255,
            0.4
        ]);
        this.clearChecked();
        this.addGeometries([joined], true);
    }

    ngOnDestroy(): void {
        this.geometries.splice(0);
        this.map?.clearPreview();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.query(this.point);
    }

    paste(): void {
        const geometries = this.mapEditService.pasteFromInternal();
        this.addGeometries(geometries, false);
    }

    query(point: IMapGeometry): void {
        if (ObjectUtils.isNullOrUndefined(point)) {
            return;
        }
        this.loading = true;
        this.mapQueryService.queryAllVisibleSublayers(point).subscribe({
            next: resultLayers => {
                this.geometries.splice(0);
                resultLayers.forEach(resultLayer => {
                    resultLayer?.forEach(resultSublayers => {
                    // eslint-disable-next-line sonarjs/no-nested-functions
                        resultSublayers.geometries?.forEach(geo => {
                            this.geometries?.push(this.getInfo(geo, resultSublayers.layerid, resultSublayers.sublayerid, resultSublayers.sublayername));
                        });
                    });
                });
                this.changeDetectorRef.detectChanges();
            },
            error: e => {
                this.loading = false;
            },
            complete: () => {
                this.loading = false;
            }
        });
        this.changeDetectorRef.detectChanges();
    }

    updateCheckedObjects(): void {
        this.checkedGeoInfo = this.geometries.filter(g => g.checked);
    }

    zoomTo(geometries: IMapGeometry[]): void {
        const graphics = geometries.map(s => this.spatialService.toArcGis(s));
        const arcExtent = this.spatialService.getArcGisExtent(graphics);
        const extent = new MapExtent(arcExtent.xmin, arcExtent.ymin, arcExtent.xmax, arcExtent.ymax);
        this.map.gotoExtent(extent);
    }

    zoomToSelected(): void {
        const selectedGeometries = this.getSelected().map(s => s.geometry);
        this.zoomTo(selectedGeometries);
    }

    private addGeometries(geometries: IMapGeometry[], checked = false): void {
        geometries.forEach(g => {
            const info = this.getInfo(g, null, null);
            this.geometries.push(info);
            info.checked = checked;
        });
    }

    private clearChecked(): void {
        this.geometries.forEach(g => (g.checked = false));
    }

    private getInfo(geo: IMapGeometry, layerid?: any, sublayerid?: string, sublayername?: string): GeometryInformation {
        let layer;
        if (layerid) {
            layer = this.mapSettings.featurelayers.find(x => x.id === layerid);
            if (!layer) {
                layer = this.mapSettings.drawLayers.find(x => x.id === layerid);
            }
        }
        const info = {
            type: this.spatialService.getType(geo),
            area: this.spatialService.getArea(geo),
            length: this.spatialService.getLength(geo),
            layer: layer ?? "",
            sublayer: sublayerid ?? "",
            sublayername: sublayername ?? ""
        } as IGeometryMetadata;
        return new GeometryInformation(geo, info, layer);
    }

    private getSelected(): GeometryInformation[] {
        return this.geometries.filter(g => g.checked);
    }

    private preview(): void {
        clearTimeout(this.previewThrottle);
        this.previewThrottle = setTimeout(() => {
            this.map?.clearPreview();
            const selected = this.getSelected().map(g => g.geometry);
            this.map?.preview(selected, [
                200,
                200,
                255,
                0.5
            ], false);
            this.changeDetectorRef.detectChanges();
        }, 250);
    }
}
